import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { 
    ArrowLeft,
    ArrowRight,
    Sparkles,
    Package,
    Users,
    Eye,
    Send,
    Calendar,
    Clock,
    Loader2,
    CheckCircle,
    X,
    Plus,
    Minus,
    RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CAMPAIGN_TYPES = [
    { value: 'new_arrivals', label: 'Nouveaux arrivages', description: 'Produits ajoutés récemment' },
    { value: 'opportunities', label: 'Opportunités', description: 'Offres exceptionnelles' },
    { value: 'promotions', label: 'Promotions', description: 'Réductions spéciales' },
    { value: 'stock_wanted', label: 'Stock recherché', description: 'Demande de stock' },
    { value: 'free', label: 'Campagne libre', description: 'Contenu personnalisé' }
];

const CreateCampaign = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    
    // Campaign data
    const [campaignType, setCampaignType] = useState('new_arrivals');
    const [campaignName, setCampaignName] = useState('');
    const [productDays, setProductDays] = useState(7);
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    
    // Content
    const [subject, setSubject] = useState('');
    const [preheader, setPreheader] = useState('');
    const [introText, setIntroText] = useState('');
    
    // Recipients
    const [recipientMode, setRecipientMode] = useState('all'); // all, filtered, manual
    const [filters, setFilters] = useState({
        brands: [],
        minOrders: null,
        lastOrderDays: null
    });
    const [availableBrands, setAvailableBrands] = useState([]);
    const [recipients, setRecipients] = useState([]);
    const [manualEmails, setManualEmails] = useState('');
    
    // Schedule
    const [scheduleMode, setScheduleMode] = useState('now'); // now, scheduled
    const [scheduledDate, setScheduledDate] = useState('');
    const [scheduledTime, setScheduledTime] = useState('10:00');
    
    // Preview
    const [previewHtml, setPreviewHtml] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    
    // Created campaign
    const [createdCampaign, setCreatedCampaign] = useState(null);

    // Fetch products based on campaign type
    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${API_URL}/admin/campaigns/preview-products`,
                {
                    campaign_type: campaignType,
                    days: productDays,
                    limit: 30
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setProducts(response.data.products || []);
            setSelectedProducts(response.data.products?.slice(0, 15) || []);
        } catch (error) {
            toast.error('Erreur lors du chargement des produits');
        } finally {
            setLoading(false);
        }
    }, [token, campaignType, productDays]);

    // Fetch available brands
    const fetchBrands = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/campaigns/available-brands`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvailableBrands(response.data.brands || []);
        } catch (error) {
            console.error('Error fetching brands');
        }
    }, [token]);

    // Generate AI content
    const generateContent = async () => {
        if (selectedProducts.length === 0) {
            toast.error('Sélectionnez au moins un produit');
            return;
        }
        
        setGenerating(true);
        try {
            const response = await axios.post(
                `${API_URL}/admin/campaigns/generate-content`,
                {
                    campaign_type: campaignType,
                    products: selectedProducts
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSubject(response.data.subject || '');
            setPreheader(response.data.preheader || '');
            setIntroText(response.data.intro_text || '');
            toast.success('Contenu généré par IA');
        } catch (error) {
            toast.error('Erreur lors de la génération');
        } finally {
            setGenerating(false);
        }
    };

    // Fetch recipients
    const fetchRecipients = async () => {
        setLoading(true);
        try {
            const filterConfig = {
                all_validated: recipientMode === 'all',
                brands: recipientMode === 'filtered' ? filters.brands : null,
                min_orders: recipientMode === 'filtered' ? filters.minOrders : null,
                last_order_days: recipientMode === 'filtered' ? filters.lastOrderDays : null,
                manual_emails: recipientMode === 'manual' ? manualEmails.split('\n').filter(e => e.trim()) : null
            };
            
            const response = await axios.post(
                `${API_URL}/admin/campaigns/preview-recipients`,
                filterConfig,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setRecipients(response.data.recipients || []);
        } catch (error) {
            toast.error('Erreur lors du chargement des destinataires');
        } finally {
            setLoading(false);
        }
    };

    // Create campaign
    const createCampaign = async () => {
        if (!campaignName.trim()) {
            toast.error('Veuillez donner un nom à la campagne');
            return;
        }
        if (!subject.trim()) {
            toast.error('Veuillez définir un objet');
            return;
        }
        if (recipients.length === 0) {
            toast.error('Aucun destinataire');
            return;
        }
        
        setLoading(true);
        try {
            let scheduledAt = null;
            if (scheduleMode === 'scheduled' && scheduledDate) {
                scheduledAt = `${scheduledDate}T${scheduledTime}:00`;
            }
            
            const response = await axios.post(
                `${API_URL}/admin/campaigns`,
                {
                    campaign_type: campaignType,
                    name: campaignName,
                    subject,
                    preheader,
                    intro_text: introText,
                    products: selectedProducts,
                    recipient_filter: {
                        all_validated: recipientMode === 'all',
                        brands: recipientMode === 'filtered' ? filters.brands : null,
                        min_orders: recipientMode === 'filtered' ? filters.minOrders : null,
                        last_order_days: recipientMode === 'filtered' ? filters.lastOrderDays : null,
                        manual_emails: recipientMode === 'manual' ? manualEmails.split('\n').filter(e => e.trim()) : null
                    },
                    scheduled_at: scheduledAt,
                    auto_generate: false
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setCreatedCampaign(response.data.campaign);
            toast.success('Campagne créée');
            setStep(5);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setLoading(false);
        }
    };

    // Send campaign
    const sendCampaign = async () => {
        if (!createdCampaign) return;
        
        setLoading(true);
        try {
            await axios.post(
                `${API_URL}/admin/campaigns/${createdCampaign.id}/send`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Envoi démarré');
            navigate(`/admin/communication/campaign/${createdCampaign.id}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally {
            setLoading(false);
        }
    };

    // Preview email
    const previewEmail = async () => {
        if (!createdCampaign) return;
        
        try {
            const response = await axios.get(
                `${API_URL}/admin/campaigns/${createdCampaign.id}/preview-email`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPreviewHtml(response.data.html);
            setShowPreview(true);
        } catch (error) {
            toast.error('Erreur de prévisualisation');
        }
    };

    // Effects
    useEffect(() => {
        fetchBrands();
    }, [fetchBrands]);

    useEffect(() => {
        if (step === 2) {
            fetchProducts();
        }
    }, [step, fetchProducts]);

    useEffect(() => {
        if (step === 3) {
            fetchRecipients();
        }
    }, [step]);

    // Toggle product selection
    const toggleProduct = (product) => {
        const isSelected = selectedProducts.find(p => p.imei === product.imei);
        if (isSelected) {
            setSelectedProducts(selectedProducts.filter(p => p.imei !== product.imei));
        } else {
            if (selectedProducts.length < 20) {
                setSelectedProducts([...selectedProducts, product]);
            } else {
                toast.error('Maximum 20 produits');
            }
        }
    };

    // Render steps
    const renderStep1 = () => (
        <div className="space-y-6">
            <div>
                <Label className="text-base font-semibold">Type de campagne</Label>
                <p className="text-sm text-slate-500 mb-4">Choisissez le type de campagne à créer</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {CAMPAIGN_TYPES.map((type) => (
                        <div
                            key={type.value}
                            onClick={() => setCampaignType(type.value)}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                campaignType === type.value
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-4 h-4 rounded-full border-2 ${
                                    campaignType === type.value
                                        ? 'border-blue-500 bg-blue-500'
                                        : 'border-slate-300'
                                }`}>
                                    {campaignType === type.value && (
                                        <CheckCircle className="w-3 h-3 text-white" />
                                    )}
                                </div>
                                <div>
                                    <p className="font-medium">{type.label}</p>
                                    <p className="text-xs text-slate-500">{type.description}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <Label htmlFor="campaignName">Nom de la campagne *</Label>
                <Input
                    id="campaignName"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="Ex: Arrivage iPhone Juillet 2025"
                    className="mt-1"
                />
            </div>

            {campaignType === 'new_arrivals' && (
                <div>
                    <Label>Période des nouveautés</Label>
                    <Select value={String(productDays)} onValueChange={(v) => setProductDays(Number(v))}>
                        <SelectTrigger className="mt-1 w-48">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">Dernières 24h</SelectItem>
                            <SelectItem value="7">7 derniers jours</SelectItem>
                            <SelectItem value="14">14 derniers jours</SelectItem>
                            <SelectItem value="30">30 derniers jours</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            )}
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold">Sélection des produits</h3>
                    <p className="text-sm text-slate-500">
                        {selectedProducts.length} / 20 produits sélectionnés
                    </p>
                </div>
                <Button variant="outline" onClick={fetchProducts} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Actualiser
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
            ) : products.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>Aucun produit trouvé pour cette période</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[500px] overflow-y-auto">
                    {products.map((product) => {
                        const isSelected = selectedProducts.find(p => p.imei === product.imei);
                        return (
                            <div
                                key={product.imei}
                                onClick={() => toggleProduct(product)}
                                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                                    isSelected
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-slate-200 hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium">
                                            {product.brand} {product.model}
                                        </p>
                                        <p className="text-sm text-slate-500">
                                            {product.storage_capacity}GB • Grade {product.grade} • {product.color}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold text-blue-600">{product.price?.toFixed(2)}€</p>
                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                            isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                                        }`}>
                                            {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold">Contenu de l'email</h4>
                    <Button onClick={generateContent} disabled={generating || selectedProducts.length === 0}>
                        {generating ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Sparkles className="w-4 h-4 mr-2" />
                        )}
                        Générer avec IA
                    </Button>
                </div>

                <div className="space-y-4">
                    <div>
                        <Label>Objet de l'email *</Label>
                        <Input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Ex: Nouveaux arrivages - iPhone & Samsung"
                            className="mt-1"
                            maxLength={60}
                        />
                        <p className="text-xs text-slate-400 mt-1">{subject.length}/60 caractères</p>
                    </div>

                    <div>
                        <Label>Pré-header</Label>
                        <Input
                            value={preheader}
                            onChange={(e) => setPreheader(e.target.value)}
                            placeholder="Texte visible dans l'aperçu de l'email"
                            className="mt-1"
                            maxLength={100}
                        />
                    </div>

                    <div>
                        <Label>Texte d'introduction</Label>
                        <Textarea
                            value={introText}
                            onChange={(e) => setIntroText(e.target.value)}
                            placeholder="Texte d'accroche pour vos clients..."
                            className="mt-1"
                            rows={3}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold">Destinataires</h3>
                <p className="text-sm text-slate-500">Sélectionnez les clients qui recevront cette campagne</p>
            </div>

            <div className="space-y-4">
                <div className="flex gap-4">
                    {['all', 'filtered', 'manual'].map((mode) => (
                        <div
                            key={mode}
                            onClick={() => setRecipientMode(mode)}
                            className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                recipientMode === mode
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            <p className="font-medium">
                                {mode === 'all' && 'Tous les clients'}
                                {mode === 'filtered' && 'Avec filtres'}
                                {mode === 'manual' && 'Sélection manuelle'}
                            </p>
                            <p className="text-xs text-slate-500">
                                {mode === 'all' && 'Tous les clients validés'}
                                {mode === 'filtered' && 'Par marque, commandes...'}
                                {mode === 'manual' && 'Entrer les emails'}
                            </p>
                        </div>
                    ))}
                </div>

                {recipientMode === 'filtered' && (
                    <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                        <div>
                            <Label>Filtrer par marques achetées</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {availableBrands.slice(0, 10).map((brand) => (
                                    <Badge
                                        key={brand}
                                        variant={filters.brands.includes(brand) ? 'default' : 'outline'}
                                        className="cursor-pointer"
                                        onClick={() => {
                                            if (filters.brands.includes(brand)) {
                                                setFilters({...filters, brands: filters.brands.filter(b => b !== brand)});
                                            } else {
                                                setFilters({...filters, brands: [...filters.brands, brand]});
                                            }
                                        }}
                                    >
                                        {brand}
                                    </Badge>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Minimum de commandes</Label>
                                <Input
                                    type="number"
                                    value={filters.minOrders || ''}
                                    onChange={(e) => setFilters({...filters, minOrders: e.target.value ? Number(e.target.value) : null})}
                                    placeholder="Ex: 3"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Dernière commande (jours)</Label>
                                <Input
                                    type="number"
                                    value={filters.lastOrderDays || ''}
                                    onChange={(e) => setFilters({...filters, lastOrderDays: e.target.value ? Number(e.target.value) : null})}
                                    placeholder="Ex: 90"
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        <Button variant="outline" onClick={fetchRecipients}>
                            Appliquer les filtres
                        </Button>
                    </div>
                )}

                {recipientMode === 'manual' && (
                    <div>
                        <Label>Emails (un par ligne)</Label>
                        <Textarea
                            value={manualEmails}
                            onChange={(e) => setManualEmails(e.target.value)}
                            placeholder="email1@exemple.com&#10;email2@exemple.com"
                            className="mt-1"
                            rows={5}
                        />
                        <Button variant="outline" className="mt-2" onClick={fetchRecipients}>
                            Vérifier les emails
                        </Button>
                    </div>
                )}
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                    <Users className="w-8 h-8 text-blue-600" />
                    <div>
                        <p className="text-2xl font-bold text-blue-900">{recipients.length}</p>
                        <p className="text-sm text-blue-700">destinataires</p>
                    </div>
                </div>
                {recipients.length > 0 && (
                    <div className="mt-3 text-sm text-blue-700">
                        Exemples: {recipients.slice(0, 3).map(r => r.company_name || r.email).join(', ')}
                        {recipients.length > 3 && '...'}
                    </div>
                )}
            </div>
        </div>
    );

    const renderStep4 = () => (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold">Programmation de l'envoi</h3>
                <p className="text-sm text-slate-500">Choisissez quand envoyer cette campagne</p>
            </div>

            <div className="flex gap-4">
                <div
                    onClick={() => setScheduleMode('now')}
                    className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        scheduleMode === 'now'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300'
                    }`}
                >
                    <div className="flex items-center gap-3">
                        <Send className="w-6 h-6 text-blue-600" />
                        <div>
                            <p className="font-medium">Envoyer maintenant</p>
                            <p className="text-xs text-slate-500">Démarrer l'envoi immédiatement</p>
                        </div>
                    </div>
                </div>

                <div
                    onClick={() => setScheduleMode('scheduled')}
                    className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        scheduleMode === 'scheduled'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300'
                    }`}
                >
                    <div className="flex items-center gap-3">
                        <Calendar className="w-6 h-6 text-amber-600" />
                        <div>
                            <p className="font-medium">Programmer</p>
                            <p className="text-xs text-slate-500">Envoyer à une date/heure précise</p>
                        </div>
                    </div>
                </div>
            </div>

            {scheduleMode === 'scheduled' && (
                <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Date d'envoi</Label>
                            <Input
                                type="date"
                                value={scheduledDate}
                                onChange={(e) => setScheduledDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label>Heure d'envoi</Label>
                            <Input
                                type="time"
                                value={scheduledTime}
                                onChange={(e) => setScheduledTime(e.target.value)}
                                className="mt-1"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Summary */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Récapitulatif</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                        <span className="text-slate-500">Campagne</span>
                        <span className="font-medium">{campaignName}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500">Type</span>
                        <span>{CAMPAIGN_TYPES.find(t => t.value === campaignType)?.label}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500">Produits</span>
                        <span>{selectedProducts.length}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500">Destinataires</span>
                        <span className="font-medium text-blue-600">{recipients.length}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500">Objet</span>
                        <span className="truncate max-w-[200px]">{subject}</span>
                    </div>
                </CardContent>
            </Card>
        </div>
    );

    const renderStep5 = () => (
        <div className="space-y-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <div>
                <h3 className="text-xl font-semibold">Campagne créée !</h3>
                <p className="text-slate-500">
                    {createdCampaign?.scheduled_at 
                        ? `Programmée pour le ${new Date(createdCampaign.scheduled_at).toLocaleString('fr-FR')}`
                        : 'Prête à être envoyée'
                    }
                </p>
            </div>

            <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={previewEmail}>
                    <Eye className="w-4 h-4 mr-2" />
                    Prévisualiser
                </Button>
                
                {!createdCampaign?.scheduled_at && (
                    <Button onClick={sendCampaign} disabled={loading} className="bg-green-600 hover:bg-green-700">
                        {loading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Send className="w-4 h-4 mr-2" />
                        )}
                        Envoyer maintenant
                    </Button>
                )}
            </div>

            <Button variant="link" onClick={() => navigate('/admin/communication')}>
                Retour au tableau de bord
            </Button>
        </div>
    );

    return (
        <AdminLayout>
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/admin/communication')}>
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Retour
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold">Nouvelle campagne</h1>
                    </div>
                </div>

                {/* Progress */}
                {step < 5 && (
                    <div className="flex items-center gap-2">
                        {[1, 2, 3, 4].map((s) => (
                            <div key={s} className="flex items-center">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                    s === step
                                        ? 'bg-blue-600 text-white'
                                        : s < step
                                            ? 'bg-green-500 text-white'
                                            : 'bg-slate-200 text-slate-500'
                                }`}>
                                    {s < step ? <CheckCircle className="w-4 h-4" /> : s}
                                </div>
                                {s < 4 && (
                                    <div className={`w-12 h-1 ${s < step ? 'bg-green-500' : 'bg-slate-200'}`} />
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Content */}
                <Card>
                    <CardContent className="pt-6">
                        {step === 1 && renderStep1()}
                        {step === 2 && renderStep2()}
                        {step === 3 && renderStep3()}
                        {step === 4 && renderStep4()}
                        {step === 5 && renderStep5()}
                    </CardContent>
                </Card>

                {/* Navigation */}
                {step < 5 && (
                    <div className="flex justify-between">
                        <Button
                            variant="outline"
                            onClick={() => setStep(Math.max(1, step - 1))}
                            disabled={step === 1}
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Précédent
                        </Button>

                        {step < 4 ? (
                            <Button
                                onClick={() => setStep(step + 1)}
                                disabled={
                                    (step === 1 && !campaignName.trim()) ||
                                    (step === 2 && (!subject.trim() || selectedProducts.length === 0)) ||
                                    (step === 3 && recipients.length === 0)
                                }
                            >
                                Suivant
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        ) : (
                            <Button
                                onClick={createCampaign}
                                disabled={loading}
                                className="bg-green-600 hover:bg-green-700"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                )}
                                Créer la campagne
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Preview Modal */}
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
                    <DialogHeader>
                        <DialogTitle>Prévisualisation de l'email</DialogTitle>
                    </DialogHeader>
                    <div 
                        className="border rounded-lg overflow-auto bg-white"
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPreview(false)}>
                            Fermer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
};

export default CreateCampaign;
